import { AvoEditorPreview } from 'components/utils/avoeditor/AvoEditorPreview';
import { SlidePanel } from 'components/utils/panels/AvoSlidePanel';
import 'css/custom.css';
import { useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { UIElement } from '../../../api/moduleElement';
import { useTriggers } from '../../../hooks/module/resources/useTriggers';
import { TextInputForm } from '../../panels/TextInputForm';
import { ModuleContext } from '../../utils/module/ModuleContext';
import { Body2, H4 } from '../../utils/typo';
import { DraggableContext, ModuleElementContext } from '../ModuleBoard';
import { GroupCardContext } from './GroupCard';
import { CardContainer } from './utils/CardContainer';

export interface TextInputFormVariable {
  module: number;
  position: number;
  container?: number;
  trigger: number;
  title: string;
  subtitle: string;
  variable: string;
  is_optional: boolean;
  default_value: any;
  default_value_tiptap: any;
}

interface TextInputCardProps {
  element: UIElement;
}

export const TextInputCard = ({ element }: TextInputCardProps) => {
  const [formOpen, setFormOpen] = useState(!element.id);
  const { setIsDragDisabled } = useContext(DraggableContext);
  const { groupCardId } = useContext(GroupCardContext);
  useEffect(() => setIsDragDisabled(formOpen), [formOpen]);

  const { module } = useContext(ModuleContext);
  const { data: triggers } = useTriggers();
  const alwayOnTrigger = triggers.find((data) => data.title === 'Always On');
  const defaultValues: TextInputFormVariable = {
    module: module!.id,
    container: groupCardId,
    trigger: element.trigger ? element.trigger.id : alwayOnTrigger?.id,
    title: element.title ?? '',
    subtitle: element.subtitle ?? '',
    variable: element.variable_item?.name ?? '',
    is_optional: !!element.is_optional,
    default_value: element.default_value,
    default_value_tiptap: element.default_value_tiptap,
    position: element.position,
  };

  const form = useForm<TextInputFormVariable>({ defaultValues });

  const { refetchModuleElements, clearModuleElements } = useContext(ModuleElementContext);
  const onSave = () => {
    try {
      refetchModuleElements();
      setFormOpen(false);
    } catch (e) {
      console.log(e);
    }
  };
  const onCancel = () => {
    clearModuleElements();
    form.reset(defaultValues);
    setFormOpen(false);
  };

  return (
    <FormProvider {...form}>
      <CardContainer
        elementId={element.id}
        onOpenPanel={() => setFormOpen(true)}
        isPreviewing={formOpen}
        variables={[{ type: 'Text', name: form.watch('variable') }]}
        hideTrigger={element.is_introduction}
      >
        <div className='space-y-1'>
          <div className='flex gap-1'>
            <H4 className='text-gray-900'>{form.watch('title')}</H4>
            {form.watch('is_optional') && <Body2 className='text-gray-700'>(optional)</Body2>}
          </div>
          <Body2 className='text-gray-900'>{form.watch('subtitle')}</Body2>
          <div className='flex min-h-10 items-center rounded border border-gray-300 px-3 py-2.5 [&_p]:!m-0'>
            <AvoEditorPreview
              contentTiptap={form.watch('default_value_tiptap')}
              contentDraftJS={form.watch('default_value')}
              isLineClampTwo
            />
          </div>
        </div>
      </CardContainer>
      <SlidePanel open={formOpen} onClose={() => setFormOpen(false)}>
        <TextInputForm textInputId={element.id} onSave={onSave} onCancel={onCancel} />
      </SlidePanel>
    </FormProvider>
  );
};
